// ActivityTracker.ts
import debug from "debug";
import { DataProvider } from "../DataVault/DataProvider.js";

const log = debug("app:tracking");

/**
 * Tracks user activity by listening to events and posting to the tracking API.
 * This class is decoupled from the rest of the application - it only listens to events.
 * @category Experience
 * @subcategory Utils
 */
class ActivityTracker {
  private experience: any;
  private eventHub: any;
  private dataProvider: DataProvider | null = null;
  private initialized = false;
  private hasTrackedInitialLogin = false; // Flag to prevent double-tracking initial SSO login
  
  // Store listener functions so we can remove them during cleanup
  private loginListener: ((data?: any) => void) | null = null;
  private logoutListener: ((data?: any) => void) | null = null;
  private failedLoginListener: ((data?: any) => void) | null = null;
  private designExportListener: ((data?: any) => void) | null = null;
  private designImportListener: ((data?: any) => void) | null = null;

  /**
   * Initializes the ActivityTracker.
   * @param {Object} experience - The experience instance containing eventHub and config.
   */
  constructor(experience: any) {
    this.experience = experience;
    this.eventHub = experience.eventHub;
    this.setupEventListeners();
    // Check if user was already authenticated before ActivityTracker was initialized
    this.checkAndTrackExistingAuth();
  }

  /**
   * Sets up event listeners for all activity tracking events.
   * Uses existing events from the codebase where possible.
   */
  private setupEventListeners(): void {
    // Login tracking - uses existing auth:login event
    this.loginListener = () => {
      // Only track if we haven't already tracked an initial login
      // This prevents double-tracking if auth:login fires after checkAndTrackExistingAuth
      if (!this.hasTrackedInitialLogin) {
        this.hasTrackedInitialLogin = true;
        this.trackActivity("Login", "");
      }
    };
    this.eventHub.on("auth:login", this.loginListener);

    // Logout tracking - uses existing sign_out event
    this.logoutListener = (data?: any) => {
      this.trackActivity("Logout", "");
      // Reset the login tracking flag so subsequent logins are tracked
      this.hasTrackedInitialLogin = false;
    };
    this.eventHub.on("sign_out", this.logoutListener);

    // Failed Login tracking - custom event for auth errors
    this.failedLoginListener = (data?: any) => {
      this.trackActivity("Failed Login", data?.details || data?.error || "");
    };
    this.eventHub.on("activity:failedLogin", this.failedLoginListener);

    // Design Export tracking - uses existing exportDesign event
    this.designExportListener = () => {
      this.trackActivity("Design Export", "");
    };
    this.eventHub.on("exportDesign", this.designExportListener);

    // Design Import tracking - uses existing designImported event
    this.designImportListener = (data?: any) => {
      const fileName = data?.name || data?.fileName || "";
      this.trackActivity("Design Import", fileName ? `File: ${fileName}` : "");
    };
    this.eventHub.on("designImported", this.designImportListener);

    this.initialized = true;
    log("ActivityTracker initialized and listening for events");
  }

  /**
   * Checks if the user was already authenticated before ActivityTracker was initialized.
   * This handles the race condition where auth:login event fires during LoginManager.init()
   * before ActivityTracker is ready to listen.
   */
  private async checkAndTrackExistingAuth(): Promise<void> {
    // Small delay to ensure LoginManager is fully initialized
    setTimeout(async () => {
      try {
        if (this.experience?.LoginManager) {
          const token = await this.experience.LoginManager.getToken();
          // If we can get a token, user is authenticated but we may have missed the auth:login event
          if (token && !this.hasTrackedInitialLogin) {
            log("User already authenticated, tracking login that may have been missed");
            this.hasTrackedInitialLogin = true;
            await this.trackActivity("Login", "");
          }
        }
      } catch (error) {
        // Silently fail - this is just a safety check
        log("Error checking existing auth status:", error);
      }
    }, 100);
  }

  /**
   * Tracks an activity by posting to the tracking API.
   * @param {string} activityType - The type of activity to track.
   * @param {string} details - Optional details about the activity.
   */
  private async trackActivity(activityType: string, details: string = ""): Promise<void> {
    try {
      // Initialize DataProvider if not already done
      if (!this.dataProvider) {
        this.dataProvider = new DataProvider(this.experience);
      }

      const payload = {
        Type: activityType,
        Details: details || "",
      };

      log(`Tracking activity: ${activityType}`, payload);

      // Use DataProvider to make the POST request
      // This automatically handles authentication via _getAuthHeaders()
      await this.dataProvider.create("management/tracking", payload, 5000);

      log(`Activity tracked successfully: ${activityType}`);
    } catch (error: any) {
      // Silently fail - tracking failures should never break the app
      log(`Error tracking activity ${activityType}:`, error);
    }
  }

  /**
   * Cleans up event listeners and resources.
   */
  cleanup(): void {
    if (!this.eventHub || !this.initialized) return;

    log("Cleaning up ActivityTracker...");

    // Remove all event listeners using stored references
    if (this.loginListener) {
      this.eventHub.off("auth:login", this.loginListener);
      this.loginListener = null;
    }
    if (this.logoutListener) {
      this.eventHub.off("sign_out", this.logoutListener);
      this.logoutListener = null;
    }
    if (this.failedLoginListener) {
      this.eventHub.off("activity:failedLogin", this.failedLoginListener);
      this.failedLoginListener = null;
    }
    if (this.designExportListener) {
      this.eventHub.off("exportDesign", this.designExportListener);
      this.designExportListener = null;
    }
    if (this.designImportListener) {
      this.eventHub.off("designImported", this.designImportListener);
      this.designImportListener = null;
    }

    // Clean up DataProvider if it exists
    if (this.dataProvider) {
      this.dataProvider.cleanup();
      this.dataProvider = null;
    }

    // Clear references
    this.experience = null;
    this.eventHub = null;
    this.initialized = false;

    log("ActivityTracker cleanup complete");
  }
}

export default ActivityTracker;

